// src/views/parent/PAStatistiques.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useParentService } from "../../services/parentService";
import { Pie } from "react-chartjs-2";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

ChartJS.register(ArcElement, Tooltip, Legend);

const COLOR_PRESENT = "#3b6d11";
const COLOR_ABSENT = "#dc3545";
const COLOR_RETARD = "#ba7517";
const COLOR_WEEKEND = "#8A5A14";
const COLOR_NON_POINTE = "#9ba5b4";

export default function PAStatistiques({ slug }) {
  const { enfantId } = useParams();
  const navigate = useNavigate();
  const { getStatsPresencesEnfant } = useParentService();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [month, setMonth] = useState(new Date());

  const hasFetchedRef = useRef(false);

  // ─── CHARGEMENT DES STATISTIQUES ────────────────────────────────────
  const loadStats = useCallback(async () => {
    if (!slug || !enfantId) {
      setError("Paramètres manquants (parent ou élève).");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const year = month.getFullYear();
      const monthNum = month.getMonth() + 1;
      const stats = await getStatsPresencesEnfant(slug, enfantId, year, monthNum);
      setData(stats);
    } catch (err) {
      setError("Impossible de récupérer les statistiques.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, enfantId, month]);

  // Chargement initial + rechargement quand le mois change
  useEffect(() => {
    // 1er appel : on respecte le garde-fou
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      loadStats();
      return;
    }
    // Changement de mois : rechargement légitime
    loadStats();
  }, [loadStats]);

  // ─── PRÉPARATION DES DONNÉES POUR LE GRAPHIQUE ────────────────────
  const pieData = {
    labels: ["Présent", "Absent", "Retard"],
    datasets: [
      {
        data: [
          data?.statistiques?.present || 0,
          data?.statistiques?.absent || 0,
          data?.statistiques?.retard || 0,
        ],
        backgroundColor: [COLOR_PRESENT, COLOR_ABSENT, COLOR_RETARD],
        borderWidth: 2,
        borderColor: "#fff",
      },
    ],
  };

  const pieOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          padding: 20,
          usePointStyle: true,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || "";
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
  };

  // ─── STYLE POUR LE CALENDRIER ──────────────────────────────────────
  const tileClassName = ({ date, view }) => {
    if (view === "month") {
      const dateStr = date.toISOString().split("T")[0];
      const detail = data?.details?.find((d) => {
        const detailDate = new Date(d.date).toISOString().split("T")[0];
        return detailDate === dateStr;
      });

      if (detail) {
        if (detail.statut === "PRESENT") return "present-day";
        if (detail.statut === "ABSENT") return "absent-day";
        if (detail.statut === "RETARD") return "retard-day";
      } else if (date.getDay() === 0 || date.getDay() === 6) {
        return "weekend-day";
      } else if (
        date.getMonth() === month.getMonth() &&
        date.getFullYear() === month.getFullYear()
      ) {
        return "no-pointage-day";
      }
    }
    return "";
  };

  // ─── RENDU ───────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap');

        *, *::before, *::after { box-sizing: border-box; }

        .stats-page {
          min-height: 100vh;
          background: var(--s2);
          padding-bottom: 24px;
        }

        /* ─── Header : bouton sur sa ligne, titre centré dessous ─── */
        .stats-header {
          background: linear-gradient(135deg, var(--pr-dk), var(--pr));
          padding: 20px 16px 32px;
          color: #fff;
          border-radius: 0 0 40px 40px;
          position: relative;
          box-shadow: 0 4px 15px rgba(0,0,0,0.15);
          overflow: hidden;
        }

        .stats-header::after {
          content: "";
          position: absolute;
          left: 20%;
          right: 20%;
          bottom: 0;
          height: 3px;
          background: linear-gradient(90deg, transparent, var(--or), transparent);
        }

        .stats-header-top {
          display: flex;
          align-items: center;
          margin-bottom: 12px;
        }

        .stats-back-btn {
          background: rgba(255,255,255,0.2);
          border: none;
          color: #fff;
          border-radius: 12px;
          cursor: pointer;
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          transition: background 0.2s;
          flex-shrink: 0;
        }

        .stats-back-btn:hover {
          background: rgba(255,255,255,0.3);
        }

        .stats-header-title {
          text-align: center;
        }

        .stats-header-title h2 {
          font-family: 'Poppins', sans-serif;
          font-size: 20px;
          font-weight: 700;
          margin: 0 0 4px;
          line-height: 1.3;
        }

        .stats-header-title p {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          opacity: 0.9;
          margin: 0;
          line-height: 1.4;
        }

        /* ─── Loader intégré ─── */
        .stats-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 20px;
          gap: 14px;
        }

        .stats-loading .spinner {
          width: 44px;
          height: 44px;
          border: 4px solid var(--pr-bg, #e5eef7);
          border-top-color: var(--pr);
          border-radius: 50%;
          animation: stats-spin 0.8s linear infinite;
        }

        @keyframes stats-spin { to { transform: rotate(360deg); } }

        .stats-loading span {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
        }

        /* ─── Carte d'erreur intégrée ─── */
        .stats-error {
          background: #fff;
          border-radius: 20px;
          padding: 40px 20px;
          text-align: center;
          box-shadow: 0 4px 18px rgba(0,0,0,0.08);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          margin: 16px;
        }

        .stats-error i.alert-icon {
          font-size: 48px;
          color: var(--rd);
        }

        .stats-error h2 {
          font-family: 'Poppins', sans-serif;
          font-size: 17px;
          font-weight: 600;
          color: var(--t);
          margin: 4px 0 0;
        }

        .stats-error p {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
          margin: 0 0 12px;
        }

        .stats-error .actions-row {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: center;
        }

        .stats-error .btn {
          border: none;
          border-radius: 30px;
          padding: 10px 22px;
          cursor: pointer;
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          font-weight: 600;
          color: #fff;
          background: var(--pr);
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: opacity 0.2s;
        }

        .stats-error .btn:hover { opacity: 0.9; }

        .stats-error .btn.btn-secondary {
          background: var(--b2);
          color: var(--t);
        }

        /* ─── Cartes contenu ─── */
        .stats-card {
          background: var(--s);
          border-radius: 16px;
          padding: 16px 20px;
          margin: 12px 16px;
          box-shadow: 0 4px 10px rgba(0,0,0,0.06);
        }

        .stats-card h4 {
          font-family: 'Poppins', sans-serif;
          color: var(--pr);
          margin: 0 0 12px;
          font-size: 16px;
          font-weight: 700;
        }

        .stats-month-selector {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .stats-month-selector label {
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          color: var(--t);
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .stats-month-selector input[type="month"] {
          padding: 8px 12px;
          border-radius: 8px;
          border: 1px solid var(--b2);
          font-size: 14px;
          font-family: inherit;
          outline: none;
          background: var(--s);
          color: var(--t);
          flex: 1;
          max-width: 200px;
        }

        .stats-month-selector input[type="month"]:focus {
          border-color: var(--pr);
          box-shadow: 0 0 0 0.25rem var(--pr-bg);
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin: 16px 0;
        }

        .stats-grid .stat-item {
          text-align: center;
          padding: 12px 8px;
          border-radius: 12px;
        }

        .stats-grid .stat-item .value {
          font-family: 'Poppins', sans-serif;
          font-size: 24px;
          font-weight: 700;
        }

        .stats-grid .stat-item .label {
          font-family: 'Inter', sans-serif;
          font-size: 12px;
          color: var(--m);
          margin-top: 4px;
        }

        .stat-present { background: var(--gn-bg); }
        .stat-present .value { color: var(--gn); }

        .stat-absent { background: var(--rd-bg); }
        .stat-absent .value { color: var(--rd); }

        .stat-retard { background: var(--am-bg); }
        .stat-retard .value { color: var(--am); }

        .stats-chart {
          max-width: 280px;
          margin: 0 auto;
        }

        .stats-calendar-wrapper {
          display: flex;
          justify-content: center;
        }

        .stats-calendar-wrapper .custom-calendar {
          border: none;
          font-family: inherit;
        }

        .stats-calendar-wrapper .custom-calendar .react-calendar__tile {
          border-radius: 50%;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .stats-calendar-wrapper .custom-calendar .react-calendar__tile--active {
          background: var(--pr);
          color: #fff;
        }

        .stats-calendar-wrapper .custom-calendar .react-calendar__navigation button {
          color: var(--pr);
          font-weight: 700;
        }

        .present-day {
          background-color: var(--gn) !important;
          color: #fff !important;
          border-radius: 50% !important;
        }

        .absent-day {
          background-color: var(--rd) !important;
          color: #fff !important;
          border-radius: 50% !important;
        }

        .retard-day {
          background-color: var(--am) !important;
          color: #fff !important;
          border-radius: 50% !important;
        }

        .weekend-day {
          background-color: var(--or-dk) !important;
          color: #fff !important;
        }

        .no-pointage-day {
          background-color: var(--b2) !important;
          color: var(--m) !important;
        }

        .stats-legend {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 16px;
          font-family: 'Inter', sans-serif;
          font-size: 12px;
        }

        .stats-legend .legend-item {
          display: flex;
          align-items: center;
          gap: 5px;
        }

        .stats-legend .legend-item .color-box {
          width: 12px;
          height: 12px;
          border-radius: 2px;
          flex-shrink: 0;
        }

        /* ─── Carte "aucune donnée" ─── */
        .stats-empty {
          background: #fff;
          border-radius: 20px;
          padding: 40px 20px;
          text-align: center;
          box-shadow: 0 4px 18px rgba(0,0,0,0.08);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          margin: 16px;
        }

        .stats-empty i {
          font-size: 48px;
          color: var(--h);
          opacity: 0.5;
        }

        .stats-empty h2 {
          font-family: 'Poppins', sans-serif;
          font-size: 17px;
          font-weight: 600;
          color: var(--t);
          margin: 4px 0 0;
        }

        .stats-empty p {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
          margin: 0;
        }

        @media (max-width: 480px) {
          .stats-header { padding: 16px 12px 28px; }
          .stats-header-title h2 { font-size: 17px; }
          .stats-header-title p { font-size: 12px; }
          .stats-grid {
            grid-template-columns: 1fr 1fr 1fr;
            gap: 8px;
          }
          .stats-grid .stat-item .value { font-size: 20px; }
          .stats-card {
            padding: 12px 14px;
            margin: 8px 12px;
          }
        }
      `}</style>

      <div className="stats-page">
        {/* Header : bouton sur sa ligne, titre centré dessous */}
        <div className="stats-header">
          <div className="stats-header-top">
            <button
              className="stats-back-btn"
              onClick={() => navigate(-1)}
              aria-label="Retour"
            >
              <i className="ti ti-arrow-left" />
            </button>
          </div>

          <div className="stats-header-title">
            <h2>
              <i className="ti ti-chart-bar" style={{ marginRight: 8 }} />
              Statistiques de présence
            </h2>
            <p>Suivi détaillé des présences et absences</p>
          </div>
        </div>

        {loading ? (
          /* ── ÉTAT CHARGEMENT ── */
          <div className="stats-loading">
            <div className="spinner"></div>
            <span>Chargement des statistiques…</span>
          </div>
        ) : error ? (
          /* ── ÉTAT ERREUR ── */
          <div className="stats-error">
            <i className="ti ti-alert-circle alert-icon" />
            <h2>Erreur de chargement</h2>
            <p>{error}</p>
            <div className="actions-row">
              <button
                className="btn"
                onClick={() => {
                  hasFetchedRef.current = false;
                  loadStats();
                }}
              >
                <i className="ti ti-refresh" />
                Réessayer
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => navigate(`/${slug}/parent/accueil`)}
              >
                <i className="ti ti-home" />
                Accueil
              </button>
            </div>
          </div>
        ) : !data ? (
          /* ── AUCUNE DONNÉE (remplace l'ancien `return null` qui faisait un écran blanc) ── */
          <div className="stats-empty">
            <i className="ti ti-chart-bar-off" />
            <h2>Aucune donnée disponible</h2>
            <p>Aucune statistique pour ce mois</p>
          </div>
        ) : (
          /* ── CONTENU ── */
          <>
            {/* Sélecteur de mois */}
            <div className="stats-card">
              <div className="stats-month-selector">
                <label>
                  <i className="ti ti-calendar" style={{ color: "var(--pr)" }} />
                  Mois :
                </label>
                <input
                  type="month"
                  value={`${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}`}
                  onChange={(e) => {
                    const [y, m] = e.target.value.split("-");
                    setMonth(new Date(parseInt(y), parseInt(m) - 1));
                  }}
                />
              </div>
            </div>

            {/* Statistiques globales */}
            <div className="stats-card">
              <h4>
                <i className="ti ti-chart-pie" style={{ marginRight: 8 }} />
                Statistiques du mois
              </h4>

              <div className="stats-grid">
                <div className="stat-item stat-present">
                  <div className="value">{data?.statistiques?.present || 0}</div>
                  <div className="label">Présent</div>
                </div>
                <div className="stat-item stat-absent">
                  <div className="value">{data?.statistiques?.absent || 0}</div>
                  <div className="label">Absent</div>
                </div>
                <div className="stat-item stat-retard">
                  <div className="value">{data?.statistiques?.retard || 0}</div>
                  <div className="label">Retard</div>
                </div>
              </div>

              {/* Graphique */}
              <div className="stats-chart">
                <Pie data={pieData} options={pieOptions} />
              </div>
            </div>

            {/* Calendrier */}
            <div className="stats-card">
              <h4>
                <i className="ti ti-calendar-stats" style={{ marginRight: 8 }} />
                Calendrier des présences
              </h4>

              <div className="stats-calendar-wrapper">
                <Calendar
                  value={month}
                  tileClassName={tileClassName}
                  className="custom-calendar"
                />
              </div>

              {/* Légende */}
              <div className="stats-legend">
                <div className="legend-item">
                  <div className="color-box" style={{ backgroundColor: COLOR_PRESENT }} />
                  <span>Présent</span>
                </div>
                <div className="legend-item">
                  <div className="color-box" style={{ backgroundColor: COLOR_ABSENT }} />
                  <span>Absent</span>
                </div>
                <div className="legend-item">
                  <div className="color-box" style={{ backgroundColor: COLOR_RETARD }} />
                  <span>Retard</span>
                </div>
                <div className="legend-item">
                  <div className="color-box" style={{ backgroundColor: COLOR_WEEKEND }} />
                  <span>Weekend</span>
                </div>
                <div className="legend-item">
                  <div className="color-box" style={{ backgroundColor: COLOR_NON_POINTE }} />
                  <span>Non pointé</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}